import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { StepFlow } from '@/components/admin/StepFlow';
import { createEvent } from '@/features/events/api';
import { EMPTY_EVENT_FORM, EventFormFields, capacityForApi, type EventFormValues } from '@/features/events/EventFormFields';
import { EventNotificationPlanPreview } from '@/features/events/EventNotificationPlanPreview';
import { fetchEventTypes } from '@/features/masters/api';
import { fetchStaff } from '@/features/staff/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso } from '@/lib/datetime';

/** イベントの個別作成（事務局のみ / 指示書 §4・§12 M4）. */
export function EventCreatePage(): ReactElement {
  const navigate = useNavigate();
  const eventTypes = useQuery({ queryKey: ['event-types'], queryFn: fetchEventTypes });
  // 種別ごとの一覧から来たときは種別が決まっている（選び直させない / D-184）
  const [searchParams] = useSearchParams();
  const fixedTypeId = searchParams.get('type');
  const [values, setValues] = useState<EventFormValues>(() => ({
    ...EMPTY_EVENT_FORM,
    type_id: fixedTypeId ?? '',
  }));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 1対1の面談（サクセス面談・1on1）の種別を選んだときだけ、相手の候補を読む（D-118）。
  // 対象の受講生は人数が多いため MemberPicker が期・コースで絞って読む（D-176）
  const selectedType = (eventTypes.data ?? []).find((t) => t.id === values.type_id);
  const isOneOnOne = (selectedType?.counterpart_role ?? null) !== null;
  const isDirectorOneOnOne = selectedType?.counterpart_role === 'director';
  const isSuccessMeeting = selectedType?.counterpart_role === 'success';
  const isMonthly = selectedType?.code === 'monthly';
  const staff = useQuery({
    queryKey: ['staff', 'all'],
    queryFn: () => fetchStaff({ includeInactive: false }),
    enabled: isOneOnOne,
  });

  // 種別固定で月例会一覧から来たとき、出欠OFF・定員なしを既定にする（D-333・D-359）
  useEffect(() => {
    if (!isMonthly) return;
    setValues((v) => {
      if (
        !v.attendance_required &&
        !v.capacity_limited &&
        !v.capacity &&
        !v.attendance_deadline_at
      ) {
        return v;
      }
      return {
        ...v,
        attendance_required: false,
        attendance_deadline_at: '',
        capacity_limited: false,
        capacity: '',
      };
    });
  }, [isMonthly]);

  // 種別未指定の /events/new は使わない（汎用一覧からの新規登録を廃止 / D-338）
  useEffect(() => {
    if (fixedTypeId === null) {
      navigate('/events', { replace: true });
    }
  }, [fixedTypeId, navigate]);

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const event = await createEvent({
        type_id: values.type_id,
        title: values.title,
        description: values.description || null,
        starts_at: isSuccessMeeting
          ? null
          : values.starts_at
            ? datetimeLocalToIso(values.starts_at)
            : null,
        ends_at: isSuccessMeeting
          ? null
          : values.ends_at
            ? datetimeLocalToIso(values.ends_at)
            : null,
        venue: values.venue || null,
        meet_url: isSuccessMeeting ? null : values.meet_url || null,
        capacity: isMonthly ? null : capacityForApi(values),
        attendance_required: isMonthly ? false : values.attendance_required,
        attendance_deadline_at:
          !isOneOnOne && !isMonthly && values.attendance_required && values.attendance_deadline_at
            ? datetimeLocalToIso(values.attendance_deadline_at)
            : null,
        has_social: false,
        participation_format: values.participation_format,
        meet_log_enabled: isOneOnOne ? false : Boolean(values.meet_log_enabled),
        pre_survey_enabled: isOneOnOne ? false : values.pre_survey_enabled,
        pre_assignment_enabled: isOneOnOne ? false : values.pre_assignment_enabled,
        post_survey_enabled: isDirectorOneOnOne
          ? values.post_survey_enabled
          : isOneOnOne
            ? false
            : values.post_survey_enabled,
        post_assignment_enabled: isOneOnOne ? false : values.post_assignment_enabled,
        day_survey_enabled: isOneOnOne || !isMonthly ? false : values.day_survey_enabled,
        director_survey_enabled: isOneOnOne || !isMonthly ? false : values.director_survey_enabled,
        ...(isOneOnOne
          ? { member_id: values.member_id, counterpart_user_id: values.counterpart_user_id }
          : {}),
      });
      navigate(`/events/${event.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  if (fixedTypeId === null) {
    return <></>;
  }

  const pageTitle =
    selectedType !== undefined ? `${selectedType.name}の新規登録` : '新規登録';

  return (
    <>
      <PageHeader
        title={pageTitle}
        extraParents={
          selectedType
            ? [{ label: `${selectedType.name}一覧`, to: `/events?type=${fixedTypeId}` }]
            : undefined
        }
      />
      <StepFlow
        steps={[
          { label: '基本情報を入力する', detail: '種別・題名・日時・会場。ここで登録すると下書きになります' },
          { label: '対象者を決める', detail: '登録後の「対象者」タブ。決めないと公開しても誰にも見えません' },
          { label: '課題・アンケートを割り当てる', detail: '「課題」タブ。必要なイベントだけ（任意）' },
          {
            label: '公開する',
            detail:
              'ここで初めて受講生に見えます。メールの予約は登録したときに入っています。公開前は時刻が来ても送らずスキップします',
          },
        ]}
        currentIndex={0}
        note="登録した時点ではまだ下書きです。続きは登録後の画面（イベント詳細）で案内します。"
      />
      <MetaBox title="基本情報">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}
        {eventTypes.isError && (
          <Notice kind="error" dismissible={false}>
            イベント種別の取得に失敗しました。
            <button
              type="button"
              onClick={() => void eventTypes.refetch()}
              className="ml-2 underline"
            >
              再試行
            </button>
          </Notice>
        )}
        <EventFormFields
          values={values}
          onChange={setValues}
          eventTypes={eventTypes.data ?? []}
          staff={staff.data ?? []}
          withCounterpart
          fixedType={fixedTypeId !== null}
          hideMeetUrlAndSchedule={isSuccessMeeting}
        />
      </MetaBox>
      {values.type_id && (
        <EventNotificationPlanPreview
          typeId={values.type_id}
          startsAt={values.starts_at}
          endsAt={values.ends_at}
        />
      )}
      <MetaBox title="登録">
        <div className="flex gap-2">
          <button
            type="button"
            disabled={
              submitting ||
              !values.type_id ||
              !values.title ||
              (!isOneOnOne && !values.starts_at) ||
              (!isOneOnOne && values.meet_log_enabled === null) ||
              (!isOneOnOne && !isMonthly && values.attendance_required && !values.attendance_deadline_at) ||
              (values.capacity_limited && !values.capacity.trim() && !isMonthly) ||
              (isOneOnOne && (!values.member_id || !values.counterpart_user_id))
            }
            onClick={() => void onSubmit()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '登録中…' : '登録する'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/events?type=${fixedTypeId}`)}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>
    </>
  );
}
